import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Camera, MapPin, Save, Mail, Loader2, Trash2, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '@/contexts/UsersContext';
import { validateName, validateVatId, validatePhone, validateEmail } from '@/lib/validator';
import ConfirmDialog from '../ui/ConfirmDialog';

import { useTranslation } from 'react-i18next';
import { getTransformedUrl } from '@/utils/supabaseImage';
import CountryPhoneInput from '../ui/CountryPhoneInput';

const SalonProfile: React.FC<{ organization: any, readOnly?: boolean }> = ({ organization, readOnly = false }) => {
    const { t } = useTranslation();
    const { updateOrganization } = useAuth();
    const { uploadShopBanner, deleteShopBanner, uploadShopLogo, deleteShopLogo } = useUsers();

    const [formData, setFormData] = useState<any>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showDeleteBanner, setShowDeleteBanner] = useState(false);
    const [showDeleteLogo, setShowDeleteLogo] = useState(false);
    const bannerRef = useRef<HTMLInputElement>(null);
    const logoRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (organization) {
            setFormData(JSON.parse(JSON.stringify(organization)));
        }
    }, [organization]);

    const handleSave = async () => {
        // Validation using shared validators
        if (!validateName(formData.name || '').isValid) {
            toast.error(t('salon.profile.validation.name_required'));
            return;
        }
        if (!validateVatId(formData.vat_id || '').isValid) {
            toast.error(t('salon.profile.validation.vat_invalid'));
            return;
        }
        if (!validatePhone(formData.phone || '').isValid) {
            toast.error(t('salon.profile.validation.phone_required'));
            return;
        }
        if (!validateEmail(formData.email || '').isValid) {
            toast.error(t('salon.profile.validation.email_invalid'));
            return;
        }

        setIsSaving(true);
        try {
            const res = await updateOrganization(formData);
            if (res.success) toast.success(t('common.save_success')); else toast.error(t('common.save_error'));
        } catch (e) { toast.error(t('common.error')); } finally { setIsSaving(false); }
    };

    const handleBanner = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const res = await uploadShopBanner(file);
            if (res.success) toast.success(t('salon.profile.banner_updated'));
        } catch (e) { toast.error(t('common.error')); } finally { setIsUploading(false); }
    };

    const confirmDeleteBanner = async () => {

        try {
            await deleteShopBanner();
            toast.success(t('salon.profile.banner_removed'));
            setShowDeleteBanner(false);
        } catch (e) { toast.error(t('common.error')); }
    };

    const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const res = await uploadShopLogo(file);
            if (res.success) toast.success(t('salon.profile.logo_updated', 'Logo aggiornato con successo'));
        } catch (e) { toast.error(t('common.error')); } finally { setIsUploading(false); }
    };

    const confirmDeleteLogo = async () => {
        try {
            await deleteShopLogo();
            toast.success(t('salon.profile.logo_removed', 'Logo rimosso con successo'));
            setShowDeleteLogo(false);
        } catch (e) { toast.error(t('common.error')); }
    };

    const inputStyle = `w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500`;

    return (
        <motion.div variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }} className="space-y-6">
            {/* BANNER E LOGO */}
            <div className="relative">
                <div className="relative h-64 w-full rounded-3xl overflow-hidden group bg-gray-100">
                    <div className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 bg-cover bg-center" style={{ backgroundImage: `url("${getTransformedUrl(organization.banner_url, 'banner') || 'https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80'}")` }} />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>

                    {/* LOGO E TESTI */}
                    <div className="absolute bottom-6 left-6 z-20 flex items-center gap-3">
                        {/* LOGO */}
                        <div className="relative group/logo">
                            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white flex items-center justify-center relative">
                                {organization.logo_url ? (
                                    <div
                                        className="w-full h-full bg-cover bg-center object-contain"
                                        style={{ backgroundImage: `url("${getTransformedUrl(organization.logo_url, 'logo') || organization.logo_url}")` }}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-tr from-orange-400 to-red-500 flex items-center justify-center text-white text-3xl font-bold">
                                        {organization.name ? organization.name.charAt(0).toUpperCase() : 'S'}
                                    </div>
                                )}

                                {!readOnly && (
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/logo:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 backdrop-blur-sm rounded-full">
                                        {!organization.logo_url ? (
                                            <button onClick={() => logoRef.current?.click()} disabled={isUploading} className="text-white hover:text-clip-300 transition-colors p-1">
                                                {isUploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                                            </button>
                                        ) : (
                                            <button onClick={() => setShowDeleteLogo(true)} className="text-red-400 hover:text-red-300 transition-colors p-1">
                                                <Trash2 size={20} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <input type="file" ref={logoRef} className="hidden" accept="image/*" onChange={handleLogo} />
                        </div>

                        {/* TESTI */}
                        <div className="text-white">
                            <h2 className="text-3xl font-bold shadow-sm">{organization.name || 'Nome Salone'}</h2>
                            <p className="opacity-90 flex items-center gap-1"><MapPin size={14} /> {organization.city}, IT</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <div className="absolute top-6 right-6 flex gap-2">
                            <button onClick={() => bannerRef.current?.click()} disabled={isUploading} className="bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all border border-white/30">
                                {isUploading ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />} {t('salon.profile.edit_cover')}
                            </button>
                            {organization.banner_url && <button onClick={() => setShowDeleteBanner(true)} className="bg-red-500/80 backdrop-blur-md hover:bg-red-600 text-white w-10 h-10 rounded-xl border border-white/30 flex items-center justify-center">
                                <Trash2 size={16} />
                            </button>}
                        </div>
                    )}
                    <input type="file" ref={bannerRef} className="hidden" accept="image/*" onChange={handleBanner} />
                </div>
            </div>

            {/* INFO */}
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{t('salon.profile.general_info')}</h3>
                    {!readOnly && (
                        <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 text-clip-600 hover:bg-clip-50 px-4 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 whitespace-nowrap">
                            {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {t('common.save_changes')}
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('salon.profile.name')}</label><input type="text" value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} className={inputStyle} disabled={readOnly} /></div>
                    <div className="space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('salon.profile.vat')}</label><input type="text" value={formData.vat_id || ''} onChange={e => setFormData({ ...formData, vat_id: e.target.value })} className={inputStyle} disabled={readOnly} /></div>
                    <div className="space-y-1 md:col-span-2"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('salon.profile.desc')}</label><textarea rows={3} value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} className={`${inputStyle} resize-none`} disabled={readOnly} /></div>
                    <div className="space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.phone')}</label><div className="relative"><CountryPhoneInput value={formData.phone || ''} onChange={val => setFormData({ ...formData, phone: val })} disabled={readOnly} placeholder="+39 333 123 4567" /></div></div>
                    <div className="space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.details.form.email')}</label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} className={`${inputStyle} pl-10`} disabled={readOnly} /></div></div>
                    <div className="space-y-1 md:col-span-2"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Instagram</label><div className="relative"><Instagram className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="text" value={formData.instagram || ''} onChange={e => setFormData({ ...formData, instagram: e.target.value })} className={`${inputStyle} pl-10`} disabled={readOnly} placeholder="https://instagram.com/tuoprofilo" /></div></div>
                    <div className="space-y-1 md:col-span-2"><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><div className="md:col-span-2 space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('salon.profile.address')}</label><div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="text" value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} className={`${inputStyle} pl-10`} disabled={true} /></div></div><div className="space-y-1"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('salon.profile.city')}</label><input type="text" value={formData.city || ''} onChange={e => setFormData({ ...formData, city: e.target.value })} className={inputStyle} disabled={true} /></div></div></div>
                    {/* Modal conferma eliminazione banner */}
                    <ConfirmDialog
                        isOpen={showDeleteBanner}
                        title={t('salon.profile.delete.title')}
                        description={t('salon.profile.delete.description')}
                        confirmLabel={t('salon.profile.delete.confirm')}
                        onConfirm={confirmDeleteBanner}
                        onCancel={() => setShowDeleteBanner(false)}
                        loading={isUploading}
                    />

                    {/* Modal conferma eliminazione logo */}
                    <ConfirmDialog
                        isOpen={showDeleteLogo}
                        title={t('salon.profile.delete_logo.title', 'Elimina Logo')}
                        description={t('salon.profile.delete_logo.description', 'Sei sicuro di voler eliminare il logo del salone? Questa azione non può essere annullata.')}
                        confirmLabel={t('salon.profile.delete_logo.confirm', 'Rimuovi logo')}
                        onConfirm={confirmDeleteLogo}
                        onCancel={() => setShowDeleteLogo(false)}
                        loading={isUploading}
                        variant="danger"
                    />
                </div>
            </div>

            {/* Modal Overlay per Caricamento tramite Portal per coprire l'intero schermo */}
            {isUploading && createPortal(
                <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center">
                    <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                    <p className="text-white font-medium text-lg">{t('common.uploading', 'Caricamento in corso...')}</p>
                </div>,
                document.body
            )}
        </motion.div>
    );
};

export default SalonProfile;
